import React, { useState, useRef, useEffect } from 'react';
import { FloatingActionBar } from './FloatingActionBar';

export function TableHeaders({ 
    allColumns, columns, fks, sortConfig, handleSort, groupOnColumn, distinctMode, deactivateGroupMode,
    columnOrder = [], setColumnOrder, columnWidths = {}, setColumnWidths,
    customColumnNames = {}, setCustomColumnNames,
    columnFilters = [], setColumnFilters,
    draggingCol, setDraggingCol, dragOverCol, setDragOverCol,
    setHiddenColumns, toggleInjectedColumn, viewSettingsControl,
    isHeadersHidden, headerVisibility, setHeaderVisibility, cascadeHideHeaders, setCascadeHideHeaders,
    addSelfSubgrid
}) {
    // visibleColumns is pre-filtered and pre-sorted by parent
    const visibleColumns = columns;
    const [editingCol, setEditingCol] = useState(null);
    const [editValue, setEditValue] = useState('');
    const [isResizing, setIsResizing] = useState(false);
    const editInputRef = useRef(null);

    useEffect(() => {
        if (editingCol && editInputRef.current) {
            editInputRef.current.focus();
            editInputRef.current.select();
        }
    }, [editingCol]);

    const handleReorder = (sourceCol, targetCol) => {
        if (!setColumnOrder) return;
        let currentOrder = columnOrder.length > 0 ? [...columnOrder] : columns.map(c => c.ColumnName);
        columns.forEach(c => {
            if (!currentOrder.includes(c.ColumnName)) {
                currentOrder.push(c.ColumnName);
            }
        });
        const newOrder = [...currentOrder];
        const sourceIdx = newOrder.indexOf(sourceCol);
        const targetIdx = newOrder.indexOf(targetCol);
        
        if (sourceIdx > -1 && targetIdx > -1) {
            newOrder.splice(sourceIdx, 1);
            newOrder.splice(targetIdx, 0, sourceCol);
            setColumnOrder(newOrder);
        }
    };

    const handleResizeStart = (e, colName) => {
        e.preventDefault();
        e.stopPropagation();
        const startX = e.clientX;
        const thElement = e.target.closest('th');
        const startWidth = columnWidths[colName] || thElement.offsetWidth;

        const onMouseMove = (moveEvent) => {
            const newWidth = Math.max(50, startWidth + (moveEvent.clientX - startX));
            if (setColumnWidths) {
                setColumnWidths(prev => ({ ...prev, [colName]: newWidth }));
            }
        };

        const onMouseUp = () => {
            setIsResizing(false);
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            document.body.classList.remove('cursor-col-resize', 'select-none');
        };

        setIsResizing(true);
        document.body.classList.add('cursor-col-resize', 'select-none');
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    };

    const handleDoubleClickResize = (e, colName) => {
        e.preventDefault();
        e.stopPropagation();
        // Find all cells for this column using data-col attribute or by index
        const thElement = e.target.closest('th');
        const colIndex = Array.from(thElement.parentNode.children).indexOf(thElement);
        const table = thElement.closest('table');
        
        let maxLength = colName.length;
        
        // Check rows
        const rows = table.querySelectorAll('tbody tr');
        rows.forEach(row => {
            // Only check standard data rows (avoid subgrids or groupings)
            if(row.children.length > colIndex) {
                const cell = row.children[colIndex];
                if (cell && cell.textContent) {
                    const textLen = cell.textContent.trim().length;
                    if (textLen > maxLength) {
                        maxLength = textLen;
                    }
                }
            }
        });

        // Calculate approx width: max 50 chars. ~8px per char + 32px padding
        const cappedLength = Math.min(maxLength, 50);
        const newWidth = (cappedLength * 8) + 32;

        if (setColumnWidths) {
            setColumnWidths(prev => ({ ...prev, [colName]: newWidth }));
        }
    };

    const columnSelectorMenu = {
        type: 'custom',
        render: () => (
            <div className="px-3 py-2 max-h-[300px] overflow-y-auto">
                <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Toggle Columns</div>
                {allColumns && allColumns.map(col => {
                    const isVisible = visibleColumns.some(c => c.ColumnName === col.ColumnName);
                    return (
                        <label key={col.ColumnName} className="flex items-center gap-2 py-1 cursor-pointer hover:bg-slate-700/50 px-2 rounded" onClick={e => e.stopPropagation()}>
                            <input 
                                type="checkbox" 
                                checked={isVisible}
                                onChange={(e) => {
                                    if (setHiddenColumns) {
                                        setHiddenColumns(prev => {
                                            const current = Array.isArray(prev) ? prev : Object.keys(prev).filter(k => prev[k]);
                                            if (e.target.checked) return current.filter(c => c !== col.ColumnName);
                                            return [...current, col.ColumnName];
                                        });
                                    }
                                }}
                                className="rounded border-slate-600 bg-slate-700 text-teal-500 focus:ring-teal-500/50 focus:ring-offset-slate-800"
                            />
                            <span className="text-xs text-slate-300">{col.ColumnName}</span>
                        </label>
                    );
                })}
            </div>
        )
    };

    return (
        <thead className="text-xs uppercase bg-slate-900 text-slate-400 sticky top-0 shadow z-10">
            {isHeadersHidden ? (
                <tr>
                    <th colSpan={visibleColumns.length + 1} className="px-2 py-0.5 bg-slate-900 border-b border-slate-700/50 h-[24px]"></th>
                    <th className="sticky right-0 top-0 bg-slate-900 border-b border-l border-slate-700/50 px-2 py-0 w-[50px] shrink-0 z-30 shadow-[-5px_0_15px_-5px_rgba(0,0,0,0.3)] h-[24px]">
                        <div className="flex items-center justify-center cursor-pointer h-full">
                            <FloatingActionBar
                                triggerElement={
                                    <button className="text-slate-400 hover:text-white transition-colors">
                                        <span className="material-symbols-outlined text-[18px]">more_horiz</span>
                                    </button>
                                }
                                menuItems={[
                                    { label: "Show Headers", icon: "unfold_more", onClick: () => { setHeaderVisibility('show'); } },
                                    { label: cascadeHideHeaders ? "Disable Cascade Hide" : "Cascade Hide Headers", icon: cascadeHideHeaders ? "layers_clear" : "layers", onClick: () => { setCascadeHideHeaders(!cascadeHideHeaders); } },
                                    { type: "divider" },
                                    { label: "Show All Columns", icon: "visibility", onClick: () => { if(setHiddenColumns) setHiddenColumns([]); } },
                                    columnSelectorMenu
                                ]}
                            />
                        </div>
                    </th>
                </tr>
            ) : (
            <tr>
                {/* Empty TH for the expand chevron column */}
                <th className="px-2 py-3 bg-slate-900 border-b border-slate-700 w-8"></th>

                {visibleColumns.map(col => {
                    const isFk = fks.some(fk => fk.ColumnName === col.ColumnName);
                    const isMultiHop = col.ColumnName.includes('->');
                    let headerText = col.ColumnName;
                    let hoverTitle = col.ColumnName;

                    if (customColumnNames[col.ColumnName]) {
                        headerText = customColumnNames[col.ColumnName];
                    } else if (isMultiHop) {
                        headerText = col.ColumnName.split('->').pop();
                        hoverTitle = col.ColumnName.split('->').join('->\n');
                    }

                    const sortState = sortConfig.find(s => s.key === col.ColumnName);
                    const widthStyle = columnWidths[col.ColumnName] ? { width: columnWidths[col.ColumnName], minWidth: columnWidths[col.ColumnName], maxWidth: columnWidths[col.ColumnName] } : {};

                    const isDragging = draggingCol === col.ColumnName;
                    const isDragOver = dragOverCol === col.ColumnName && !isDragging;

                    return (
                        <th 
                            key={col.ColumnName} 
                            title={hoverTitle} 
                            className={`relative px-4 py-3 bg-slate-700 whitespace-nowrap group truncate transition-opacity border-r border-slate-600 ${isDragging ? 'opacity-0' : 'opacity-100'} ${isDragOver ? 'border-l-4 border-blue-500' : ''}`}
                            style={widthStyle}
                            draggable={!isResizing}
                            onDragStart={(e) => {
                                e.dataTransfer.setData('colName', col.ColumnName);
                                if (setDraggingCol) setDraggingCol(col.ColumnName);
                                // Required to allow browser to capture the ghost image before we set opacity 0
                                setTimeout(() => {
                                    if (e.target) e.target.style.opacity = '0';
                                }, 0);
                            }}
                            onDragEnd={(e) => {
                                if (setDraggingCol) setDraggingCol(null);
                                if (setDragOverCol) setDragOverCol(null);
                                e.target.style.opacity = '';
                            }}
                            onDragOver={(e) => {
                                e.preventDefault();
                                if (dragOverCol !== col.ColumnName) {
                                    if (setDragOverCol) setDragOverCol(col.ColumnName);
                                }
                            }}
                            onDragLeave={() => {
                                if (dragOverCol === col.ColumnName) {
                                    if (setDragOverCol) setDragOverCol(null);
                                }
                            }}
                            onDrop={(e) => {
                                e.preventDefault();
                                if (setDragOverCol) setDragOverCol(null);
                                if (setDraggingCol) setDraggingCol(null);
                                const sourceCol = e.dataTransfer.getData('colName');
                                if (sourceCol && sourceCol !== col.ColumnName) {
                                    handleReorder(sourceCol, col.ColumnName);
                                }
                            }}
                        >
                            <div className="flex items-center gap-1 overflow-hidden">
                                <span 
                                    className="cursor-pointer hover:text-white transition-colors flex-1 truncate select-none"
                                    onClick={() => handleSort(col.ColumnName)}
                                    onDoubleClick={(e) => {
                                        e.stopPropagation();
                                        setEditingCol(col.ColumnName);
                                        setEditValue(headerText);
                                    }}
                                >
                                    {editingCol === col.ColumnName ? (
                                        <input
                                            ref={editInputRef}
                                            type="text"
                                            className="bg-slate-900 border border-teal-500 text-slate-200 px-1 py-0.5 rounded text-xs w-full max-w-[120px]"
                                            value={editValue}
                                            onChange={(e) => setEditValue(e.target.value)}
                                            onBlur={() => {
                                                if (editValue.trim()) {
                                                    setCustomColumnNames(prev => ({...prev, [col.ColumnName]: editValue.trim()}));
                                                } else {
                                                    setCustomColumnNames(prev => {
                                                        const copy = {...prev};
                                                        delete copy[col.ColumnName];
                                                        return copy;
                                                    });
                                                }
                                                setEditingCol(null);
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.target.blur();
                                                } else if (e.key === 'Escape') {
                                                    setEditingCol(null);
                                                }
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                            onDoubleClick={(e) => e.stopPropagation()}
                                        />
                                    ) : (
                                        <>
                                            {viewSettingsControl && col.ColumnName === visibleColumns[0]?.ColumnName && (
                                                <div className="inline-block mr-2 align-middle">
                                                    {viewSettingsControl}
                                                </div>
                                            )}
                                            {headerText}
                                            {col.IsPrimaryKey && <span className="ml-1 text-yellow-500" title="Primary Key">🔑</span>}
                                            {col.IsIdentity && <span className="ml-1 text-slate-500" title="Auto-Increment Identity">⚡</span>}
                                            {sortState && (
                                                <span className="ml-1 text-blue-400">
                                                    {sortState.direction === 'asc' ? '▲' : '▼'}
                                                </span>
                                            )}
                                        </>
                                    )}
                                </span>
                                
                                <div className="opacity-0 group-hover:opacity-100 flex items-center gap-1 transition-opacity shrink-0">
                                    <FloatingActionBar
                                        menuItems={[
                                            { label: "Rename", icon: "edit", onClick: (e) => { e.stopPropagation(); setEditingCol(col.ColumnName); setEditValue(headerText); } },
                                            ...(distinctMode ? [{
                                                label: "Ungroup (Restore Columns)",
                                                icon: "format_clear",
                                                onClick: (e) => {
                                                    e.stopPropagation();
                                                    if (deactivateGroupMode) deactivateGroupMode();
                                                }
                                            }] : [{
                                                label: "Group by this column", 
                                                icon: "view_list", 
                                                onClick: (e) => { 
                                                    e.stopPropagation(); 
                                                    if (groupOnColumn) groupOnColumn(col.ColumnName, allColumns || columns);
                                                }
                                            }]),
                                            { label: "Hide this column", icon: "visibility_off", onClick: (e) => { e.stopPropagation(); if(setHiddenColumns) setHiddenColumns(prev => [...prev, col.ColumnName]); } },
                                            { type: "divider" },
                                            {
                                                type: "custom",
                                                render: () => (
                                                    <div className="px-3 py-1.5" onClick={(e) => e.stopPropagation()}>
                                                        <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1 block">Filter column</label>
                                                        <input 
                                                            type="text" 
                                                            className="bg-slate-900 border border-slate-600 text-slate-200 px-2 py-1 rounded text-xs w-full focus:border-teal-500 focus:outline-none" 
                                                            placeholder="Search..."
                                                            defaultValue={columnFilters?.find(f => f.column === col.ColumnName)?.value || ''}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') {
                                                                    const val = e.target.value.trim();
                                                                    if (setColumnFilters) {
                                                                        setColumnFilters(prev => {
                                                                            const filtered = prev.filter(f => f.column !== col.ColumnName);
                                                                            if (val) {
                                                                                filtered.push({ column: col.ColumnName, value: val });
                                                                            }
                                                                            return filtered;
                                                                        });
                                                                    }
                                                                    // We could optionally close the menu here, but since FloatingActionBar doesn't pass a close handler to custom renders, we'll let it stay open or user can click away.
                                                                    // Or we can simulate a click outside
                                                                    document.body.click();
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                )
                                            },
                                            { type: "divider" },
                                            { label: "Add Self Subgrid", icon: "account_tree", onClick: (e) => { e.stopPropagation(); if (addSelfSubgrid) addSelfSubgrid(); } },
                                            { label: "Mark as Primary Key", icon: "key", onClick: () => alert("Schema editing coming soon!") },
                                            { label: "Link to Table...", icon: "link", onClick: () => alert("Schema editing coming soon!") },
                                        ]}
                                    />
                                </div>
                            </div>
                            
                            {/* Resize Handle */}
                            <div 
                                className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize bg-transparent hover:bg-teal-500/50 z-20"
                                onMouseDown={(e) => handleResizeStart(e, col.ColumnName)}
                                onDoubleClick={(e) => handleDoubleClickResize(e, col.ColumnName)}
                            />
                        </th>
                    );
                })}
                <th className="sticky right-0 top-0 bg-slate-900 border-b border-l border-slate-700/50 px-3 py-3 w-[50px] shrink-0 z-30 shadow-[-5px_0_15px_-5px_rgba(0,0,0,0.3)]">
                    <div className="flex items-center justify-center cursor-pointer group/headerkebab">
                        <FloatingActionBar
                            menuItems={[
                                { label: isHeadersHidden ? "Show Headers" : "Collapse Headers", icon: isHeadersHidden ? "unfold_more" : "unfold_less", onClick: () => { setHeaderVisibility(isHeadersHidden ? 'show' : 'hide'); } },
                                { label: cascadeHideHeaders ? "Disable Cascade Hide" : "Cascade Hide Headers", icon: cascadeHideHeaders ? "layers_clear" : "layers", onClick: () => { setCascadeHideHeaders(!cascadeHideHeaders); } },
                                { type: "divider" },
                                { label: "Show All Columns", icon: "visibility", onClick: () => { if(setHiddenColumns) setHiddenColumns([]); } }
                            ]}
                        />
                    </div>
                </th>
            </tr>
            )}
        </thead>
    );
}
